import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, FormGroup, FormFeedback, Label, CardTitle, CardText, Alert } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEquals, faTimes } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import Switch from 'react-switch';
import ButtonIcon from '../../common/ButtonIcon';
import CMDBCompanyCheck from '../ServiceNow/CMDBCompanyCheck';
import CMDBCompany from '../ServiceNow/CMDBCompany';
import CMDBAffectedCIQuery from '../ServiceNow/CMDBAffectedCIQuery';

/**
 * ServiceNow Settings accordion content for vendor configuration wizard.
 * Combines CMDB company, affected CI query, auto-close toggle, and
 * priority mapping into a single section.
 */
export default function ServiceNowSettingsContent({
  vendorId,
  vendor,
  vendorLabel,
  snCompanySysId,
  setSnCompanySysId,
  snCompanySysIdOptions,
  snCompanySysIdErrors,
  fields,
  handleFieldChange,
  isSubmitted,
  priorityMaps,
  setPriorityMaps,
  vendorPriorityOptions,
  snPriorityOptions,
  autoClose,
  setAutoClose,
  enableChatGptPriority
}) {
  const selectStyles = {
    option: (styles, { isDisabled, isFocused, isSelected }) => ({
      ...styles,
      color: isFocused || isSelected ? 'white' : 'var(--ink)'
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 })
  };

  const blankPriority = { vendorPriority: null, snPriority: null };

  const addPriority = () => {
    setPriorityMaps([...priorityMaps, { ...blankPriority }]);
  };

  const removePriority = idx => {
    priorityMaps.splice(idx, 1);
    setPriorityMaps([...priorityMaps]);
  };

  const handleCatChange = (e, idx, field) => {
    const updatedPriorities = [...priorityMaps];
    updatedPriorities[idx][field] = e;
    setPriorityMaps(updatedPriorities);
  };

  return (
    <>
      <Row>
        <Col lg="6">
          {snCompanySysIdErrors === false ? (
            <CMDBCompanyCheck
              snCompanySysIdOptions={snCompanySysIdOptions}
              setSnCompanySysId={setSnCompanySysId}
              snCompanySysId={snCompanySysId}
              isSubmitted={isSubmitted}
            />
          ) : (
            <CMDBCompany
              snCompanySysId={fields.snCompanySysId}
              handleFieldChange={handleFieldChange}
              isSubmitted={isSubmitted}
            />
          )}
        </Col>
        <Col lg="6">{/* placeholder */}</Col>
      </Row>

      {vendor.cloudVendor !== 1 ? (
        <CMDBAffectedCIQuery
          snAffectedCIQuery={fields.snAffectedCIQuery}
          handleFieldChange={handleFieldChange}
          isSubmitted={isSubmitted}
        />
      ) : null}

      {setAutoClose && (
        <>
          <Row>
            <Col>
              <Label>Select whether you want to auto close bug tickets in ServiceNow</Label>
            </Col>
          </Row>
          <Row>
            <Col>
              <Switch
                onChange={() => setAutoClose(!autoClose)}
                checked={autoClose}
                onColor="#253746"
                uncheckedIcon={false}
                checkedIcon={false}
                height={20}
                width={40}
                handleDiameter={16}
              />
            </Col>
          </Row>
        </>
      )}

      <hr className="my-3" />

      {/* Priority Mapping */}
      <CardTitle data-testid={'priorities-title'}>
        Map Vendor Severity to ServiceNow Priority
        <span style={{ color: '#bd472a' }}>*</span>
      </CardTitle>
      <CardText>Specify which severities and priorities to monitor</CardText>

      {!!priorityMaps?.length && (
        <Fragment>
          <Row>
            <Col lg={5}>
              <FormGroup>
                <Label>Vendor Severity</Label>
              </FormGroup>
            </Col>
            <Col lg={1}>
              <FormGroup style={{ textAlign: 'center', marginTop: '38px' }} />
            </Col>
            <Col lg={5}>
              <FormGroup>
                <Label>ServiceNow Impact / Urgency / Priority</Label>
              </FormGroup>
            </Col>
          </Row>
        </Fragment>
      )}

      {priorityMaps?.map((val, idx) => {
        const vendorPriority = `vendor-${idx}`;
        const snPriority = `snPriority-${idx}`;
        return (
          <Fragment key={`priority-${idx}`}>
            <Row>
              <Col md={5}>
                <FormGroup>
                  <div data-testid={'severity-select'}>
                    <Select
                      id={vendorPriority}
                      name={vendorPriority}
                      value={priorityMaps[idx].vendorPriority}
                      onChange={e => handleCatChange(e, idx, 'vendorPriority')}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      options={
                        enableChatGptPriority
                          ? vendorPriorityOptions
                              ?.filter(o => {
                                return o.hasOwnProperty('isCustom') ? o.isCustom : true;
                              })
                              ?.filter(vendorPriorityOption => {
                                const result = priorityMaps.filter(e => {
                                  if (!e.vendorPriority) {
                                    return false;
                                  }
                                  return e.vendorPriority.value === vendorPriorityOption.value;
                                });
                                return result.length ? false : true;
                              })
                          : vendorPriorityOptions
                              ?.filter(o => {
                                return !o.isCustom;
                              })
                              ?.filter(vendorPriorityOption => {
                                const result = priorityMaps.filter(e => {
                                  if (!e.vendorPriority) {
                                    return false;
                                  }
                                  return e.vendorPriority.value === vendorPriorityOption.value;
                                });
                                return result.length ? false : true;
                              })
                      }
                      theme={theme => ({
                        ...theme,
                        borderRadius: 5,
                        colors: {
                          ...theme.colors,
                          primary25: '#253746',
                          primary: '#253746'
                        }
                      })}
                      styles={selectStyles}
                      required
                    />
                  </div>
                  <FormFeedback
                    className={!priorityMaps[idx].vendorPriority && isSubmitted ? 'd-block' : ''}
                    type="invalid"
                    data-testid={'severity-feedback'}
                  >
                    Please select a priority.
                  </FormFeedback>
                </FormGroup>
              </Col>
              <Col md={1}>
                <FormGroup style={{ textAlign: 'center', marginTop: '7px' }}>
                  <FontAwesomeIcon icon={faEquals} color={'#253746'} />
                </FormGroup>
              </Col>
              <Col md={5}>
                <FormGroup>
                  <div data-testid={'priority-select'}>
                    <Select
                      id={snPriority}
                      name={snPriority}
                      value={priorityMaps[idx].snPriority}
                      onChange={e => handleCatChange(e, idx, 'snPriority')}
                      options={snPriorityOptions}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      required
                      theme={theme => ({
                        ...theme,
                        borderRadius: 5,
                        colors: {
                          ...theme.colors,
                          primary25: '#253746',
                          primary: '#253746'
                        }
                      })}
                      styles={selectStyles}
                    />
                  </div>
                  <FormFeedback
                    className={!priorityMaps[idx].snPriority && isSubmitted ? 'd-block' : ''}
                    type="invalid"
                    data-testid={'priority-feedback'}
                  >
                    Please select a priority.
                  </FormFeedback>
                </FormGroup>
              </Col>
              <Col md={1}>
                <FormGroup style={{ textAlign: 'center', marginTop: '7px' }}>
                  <FontAwesomeIcon
                    icon={faTimes}
                    color={'#9b9b9b'}
                    size="lg"
                    style={{ cursor: 'pointer' }}
                    onClick={() => removePriority(idx)}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Fragment>
        );
      })}

      <Row>
        <Col lg="5">
          <FormGroup>
            <FormFeedback className={!priorityMaps?.length && isSubmitted ? 'd-block' : ''} type="invalid">
              <Alert color="danger" fade={false}>
                Please add a priority.
              </Alert>
            </FormFeedback>
          </FormGroup>
          <FormGroup className="d-flex justify-content-end">
            <ButtonIcon
              onClick={addPriority}
              color="falcon-default"
              size="sm"
              icon="plus"
              iconClassName="fs--2"
              data-testid={'add-priority-btn'}
            >
              Add priority
            </ButtonIcon>
          </FormGroup>
        </Col>
      </Row>
    </>
  );
}

ServiceNowSettingsContent.propTypes = {
  vendorId: PropTypes.string,
  vendor: PropTypes.object,
  vendorLabel: PropTypes.string,
  snCompanySysId: PropTypes.object,
  setSnCompanySysId: PropTypes.func,
  snCompanySysIdOptions: PropTypes.object,
  snCompanySysIdErrors: PropTypes.bool,
  fields: PropTypes.object,
  handleFieldChange: PropTypes.func,
  isSubmitted: PropTypes.bool,
  priorityMaps: PropTypes.array,
  setPriorityMaps: PropTypes.func,
  vendorPriorityOptions: PropTypes.array,
  snPriorityOptions: PropTypes.array,
  autoClose: PropTypes.bool,
  setAutoClose: PropTypes.func,
  enableChatGptPriority: PropTypes.bool
};
